import { Component, createRef, memo } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Circle,
  Group,
  Layer,
  Line,
  Rect,
  Shape,
  Stage,
  Text,
} from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import nipplejs from "nipplejs";

import "../../scss/components/_field.scss";

class FieldCanvas extends Component {
  constructor(props) {
    super(props);

    this.state = {
      points: [],
      zonePoints: [],
      active: null,
      spaceHeld: false,
      stick: {
        x: 0,
        y: 0,
      },
    };

    this.defaultWidth = 1030;
    this.defaultRadius = 8;
    this.defaultStickForce = 0.05;

    this.nippleOptions = {
      color: "#ffffff",
      size: 100,
      mode: "dynamic",
    };

    this.zoneFieldPoints = [
      // Penalty area left (red)
      { id: "penalty_left_0", x: 0.05, y: 0.30, color: "#FF6B6B", desc: "Штрафная лев. ↖", group: "penalty_left" },
      { id: "penalty_left_1", x: 0.20, y: 0.30, color: "#FF6B6B", desc: "Штрафная лев. ↗", group: "penalty_left" },
      { id: "penalty_left_2", x: 0.20, y: 0.70, color: "#FF6B6B", desc: "Штрафная лев. ↘", group: "penalty_left" },
      { id: "penalty_left_3", x: 0.05, y: 0.70, color: "#FF6B6B", desc: "Штрафная лев. ↙", group: "penalty_left" },
      // Penalty area right (red)
      { id: "penalty_right_0", x: 0.80, y: 0.30, color: "#FF6B6B", desc: "Штрафная прав. ↖", group: "penalty_right" },
      { id: "penalty_right_1", x: 0.95, y: 0.30, color: "#FF6B6B", desc: "Штрафная прав. ↗", group: "penalty_right" },
      { id: "penalty_right_2", x: 0.95, y: 0.70, color: "#FF6B6B", desc: "Штрафная прав. ↘", group: "penalty_right" },
      { id: "penalty_right_3", x: 0.80, y: 0.70, color: "#FF6B6B", desc: "Штрафная прав. ↙", group: "penalty_right" },
      // Goal area left (blue)
      { id: "goal_area_left_0", x: 0.05, y: 0.38, color: "#4FC3F7", desc: "Вратарская лев. ↖", group: "goal_area_left" },
      { id: "goal_area_left_1", x: 0.10, y: 0.38, color: "#4FC3F7", desc: "Вратарская лев. ↗", group: "goal_area_left" },
      { id: "goal_area_left_2", x: 0.10, y: 0.62, color: "#4FC3F7", desc: "Вратарская лев. ↘", group: "goal_area_left" },
      { id: "goal_area_left_3", x: 0.05, y: 0.62, color: "#4FC3F7", desc: "Вратарская лев. ↙", group: "goal_area_left" },
      // Goal area right (blue)
      { id: "goal_area_right_0", x: 0.90, y: 0.38, color: "#4FC3F7", desc: "Вратарская прав. ↖", group: "goal_area_right" },
      { id: "goal_area_right_1", x: 0.95, y: 0.38, color: "#4FC3F7", desc: "Вратарская прав. ↗", group: "goal_area_right" },
      { id: "goal_area_right_2", x: 0.95, y: 0.62, color: "#4FC3F7", desc: "Вратарская прав. ↘", group: "goal_area_right" },
      { id: "goal_area_right_3", x: 0.90, y: 0.62, color: "#4FC3F7", desc: "Вратарская прав. ↙", group: "goal_area_right" },
      // Goalposts left (yellow)
      { id: "goalpost_left_0", x: 0.05, y: 0.43, color: "#FFD700", desc: "Штанга лев. верх", group: "goalpost_left" },
      { id: "goalpost_left_1", x: 0.05, y: 0.57, color: "#FFD700", desc: "Штанга лев. низ", group: "goalpost_left" },
      // Goalposts right (yellow)
      { id: "goalpost_right_0", x: 0.95, y: 0.43, color: "#FFD700", desc: "Штанга прав. верх", group: "goalpost_right" },
      { id: "goalpost_right_1", x: 0.95, y: 0.57, color: "#FFD700", desc: "Штанга прав. низ", group: "goalpost_right" },
    ];

    this.defaultFieldPoints = [
      {
        id: "0",
        x: 0.1,
        y: 0.25,
        color: "#6200EE",
        desc: "Top Left",
      }, // Top Left
      {
        id: "1",
        x: 0.5,
        y: 0.25,
        color: "#03DAc6",
        desc: "Top Middle",
      }, // Top Middle
      {
        id: "2",
        x: 0.9,
        y: 0.25,
        color: "#B00020",
        desc: "Top Right",
      }, // Top Right
      {
        id: "3",
        x: 0.9,
        y: 0.75,
        color: "#FFDE03",
        desc: "Bottom Right",
      }, // Bottom Right
      {
        id: "4",
        x: 0.5,
        y: 0.75,
        color: "#61D800",
        desc: "Bottom Middle",
      }, // Bottom Middle
      {
        id: "5",
        x: 0.1,
        y: 0.75,
        color: "#90CAF9",
        desc: "Bottom Left",
      }, // Bottom Left
      {
        id: "6",
        x: 0.1,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle Left",
      }, // Middle Left
      {
        id: "7",
        x: 0.5,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle",
      }, // Middle Middle
      {
        id: "8",
        x: 0.9,
        y: 0.5,
        color: "#90CAF9",
        desc: "Middle Right",
      }, // Middle Right
    ];

    this.stage = createRef();
    this.container = createRef();
    this.nipple = createRef();

    this.stickListener = this.stickListener.bind(this);
    this.movePoint = this.movePoint.bind(this);

    this.changeActive = this.changeActive.bind(this);
    this.resetPoints = this.resetPoints.bind(this);

    this.canvasRender = this.canvasRender.bind(this);
    this.handleCanvasPointerDown = this.handleCanvasPointerDown.bind(this);
    this.handleCanvasKeyDown = this.handleCanvasKeyDown.bind(this);
    this.handleCanvasKeyPress = this.handleCanvasKeyPress.bind(this);

    this.generatePoints = this.generatePoints.bind(this);
    this.generateFieldPoints = this.generateFieldPoints.bind(this);

    this.handlePointClick = this.handlePointClick.bind(this);
    this.handlePointDragStart = this.handlePointDragStart.bind(this);
    this.handlePointDragMove = this.handlePointDragMove.bind(this);
    this.handlePointDragEnd = this.handlePointDragEnd.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.generateCustomShape = this.generateCustomShape.bind(this);

    this.handleSwitchMarkupX = this.handleSwitchMarkupX.bind(this);
    this.handleSwitchMarkupY = this.handleSwitchMarkupY.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.handleStageDragMove = this.handleStageDragMove.bind(this);
    this.handleResetZoom = this.handleResetZoom.bind(this);
    this.handleZonePointDragStart = this.handleZonePointDragStart.bind(this);
    this.handleZonePointDragMove = this.handleZonePointDragMove.bind(this);
    this.handleZonePointDragEnd = this.handleZonePointDragEnd.bind(this);
    this.handleZonePointClick = this.handleZonePointClick.bind(this);
  }

  movePoint() {
    const points = [...this.state.points];
    const pointIndex = points.findIndex(
      (point) => point.id === this.state.active
    );
    if (pointIndex < 0) return;
    const point = points[pointIndex];
    let { x, y } = point;
    x = +(x + this.state.stick.x);
    y = +(y + this.state.stick.y);

    point.x = x;
    point.y = y;
    points[pointIndex] = point;

    this.setState((state) => ({ ...state, points: [...points] }));
  }

  changeActive(active) {
    this.setState((state) => ({ ...state, active }));
  }

  resetPoints(points) {
    let targetPoints;
    if (points && points.length > 0) {
      targetPoints = this.restoreFieldPoints(points);
    } else {
      targetPoints = this.generateFieldPoints();
    }
    this.setState((state) => ({
      ...state,
      points: targetPoints,
      active: null,
    }));
  }

  canvasRender() {
    /* Update Stage width & height according to parent container. */
    const stage = this.stage.current;
    const container = this.container.current;
    if (stage) {
      stage.width(container.offsetWidth);
      stage.height(container.offsetHeight);
    }
  }

  componentDidMount() {
    if (this.state.points.length === 0) {
      /* Generate default field & zone points if there is not saved TrackMatching. */
      const points = this.generateFieldPoints();
      this.setState((state) => ({ ...state, points }));
    }
    document.addEventListener("keyup", this.handleCanvasKeyPress);
    document.addEventListener("keydown", this.handleCanvasKeyDown);
    window.addEventListener("resize", this.handleWindowResize);
    this.manager = nipplejs.create({
      ...this.nippleOptions,
      zone: this.nipple.current,
    });
    this.stickListener(this.manager);
    this.canvasRender();
  }

  componentDidUpdate() {
    this.canvasRender();
    this.props.onUpdate();
  }

  componentWillUnmount() {
    document.removeEventListener("keyup", this.handleCanvasKeyPress);
    document.removeEventListener("keydown", this.handleCanvasKeyDown);
    window.removeEventListener("resize", this.handleWindowResize);
  }

  generatePoints(data) {
    return data.map((point) => ({
      id: point.id,
      x: point.x,
      y: point.y,
      color: point.color,
      desc: point.desc,
      isDraggable: false,
      ref: createRef(),
    }));
  }

  generateFieldPoints() {
    /* Generate default football field TrackMatching with 6 points: left, middle, right for top and bottom lines. */
    return this.generatePoints(this.defaultFieldPoints);
  }

  generateZonePoints() {
    return this.generatePoints(this.zoneFieldPoints);
  }

  resetZonePoints(zonePoints) {
    let targetPoints;
    if (zonePoints && zonePoints.length > 0) {
      targetPoints = this.restoreZonePoints(zonePoints);
    } else {
      targetPoints = this.generateZonePoints();
    }
    this.setState((state) => ({ ...state, zonePoints: targetPoints }));
  }

  restoreZonePoints(points) {
    const targetPoints = [];
    for (const source of points) {
      const target = this.zoneFieldPoints.find((p) => p.id === source.id);
      if (target) {
        targetPoints.push({ ...this.generatePoints([target])[0], ...source });
      }
    }
    // Add any zone points not in saved data with defaults
    for (const def of this.zoneFieldPoints) {
      if (!targetPoints.find((p) => p.id === def.id)) {
        targetPoints.push(this.generatePoints([def])[0]);
      }
    }
    return targetPoints;
  }

  restoreFieldPoints(points) {
    const targetPoints = [];
    for (let index = 0; index < points.length; index += 1) {
      const source = points[index];
      const target = this.defaultFieldPoints.find(
        (point) => point.id === source.id
      );
      targetPoints.push({ ...target, ...source });
    }
    return targetPoints;
  }

  handlePointDragStart(e) {
    const { target } = e;
    const id = target.id();

    const points = [
      ...this.state.points.map((point) => ({
        ...point,
        isDragging: point.id === id,
      })),
    ];
    this.setState((state) => ({ ...state, points, active: id }));
  }

  generateCustomShape(context, shape) {
    const container = this.container.current;

    context.beginPath();
    let { points } = this.state;
    context.moveTo(
      points[0].x * container.offsetWidth,
      points[0].y * container.offsetHeight
    );
    if (points.length === 9) {
      points = [
        points[0],
        points[1],
        points[2],
        points[8],
        ...points.slice(3, 7),
      ];
    }
    for (let i = 0; i < points.length; i++) {
      context.lineTo(
        points[i].x * container.offsetWidth,
        points[i].y * container.offsetHeight
      );
    }
    context.closePath();
    // (!) Konva specific method, it is very important
    context.fillStrokeShape(shape);
  }

  _getPointerNormalized(e) {
    const container = this.container.current;
    const rect = container.getBoundingClientRect();
    // getBoundingClientRect accounts for CSS transform automatically
    const x = (e.evt.clientX - rect.left) / rect.width;
    const y = (e.evt.clientY - rect.top) / rect.height;
    return { x: +x, y: +y };
  }

  handlePointDragMove(e) {
    const id = e.target.id();
    const pointIndex = this.state.points.findIndex((point) => point.id === id);
    const points = [...this.state.points];
    const point = points[pointIndex];

    e.target.x(0);
    e.target.y(0);

    const { x, y } = this._getPointerNormalized(e);
    point.x = x;
    point.y = y;
    points[pointIndex] = point;

    this.setState((state) => ({ ...state, points: [...points] }));
  }

  handlePointDragEnd(e) {
    const id = e.target.id();
    const point = this.state.points.find((point) => point.id === id);
    point.isDragging = false;
  }

  handlePointClick(e) {
    const id = e.target.id();
    this.setState((state) => ({ ...state, active: id }));
  }

  handleCanvasPointerDown(e) {
    if (e.target !== this.nipple.current) {
      const container = this.container.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;

      // Find nearest point within hit radius (rect.width already includes CSS scale)
      const hitRadius = 25 / rect.width;

      const allPoints = [
        ...this.state.points,
        ...(this.props.state?.secondStageEditor ? this.state.zonePoints : []),
      ];

      let closest = null;
      let closestDist = Infinity;
      for (const p of allPoints) {
        const d = Math.hypot(p.x - nx, p.y - ny);
        if (d < closestDist) {
          closestDist = d;
          closest = p;
        }
      }

      if (closest && closestDist < hitRadius) {
        this.setState((state) => ({ ...state, active: closest.id }));
      } else {
        this.setState((state) => ({ ...state, active: null }));
      }
    }
  }

  handleCanvasKeyDown(e) {
    if (e.code === "Space" && !this.state.spaceHeld) {
      e.preventDefault();
      this.setState((state) => ({ ...state, spaceHeld: true }));
    }
  }

  handleCanvasKeyPress(e) {
    switch (e.key) {
      case "Escape":
        this.setState((state) => ({ ...state, active: null }));
        break;
      case " ":
        this.setState((state) => ({ ...state, spaceHeld: false }));
        break;
      default:
        break;
    }
  }

  handleWindowResize() {
    /* Force canvas to render if the viewport has been resized. */
    this.canvasRender();
    this.setState((state) => ({ ...state }));
  }

  stickListener(manager) {
    let interval;
    manager.on("added", () => {
      interval = setInterval(() => {
        this.movePoint();
      }, 25);
    });
    manager.on("move", (e, stick) => {
      const container = this.container.current;
      const offsetY = +(
        ((stick.distance * Math.sin(stick.angle.radian)) /
          container.offsetHeight) *
        this.defaultStickForce *
        -1
      );
      const offsetX = +(
        ((stick.distance * Math.cos(stick.angle.radian)) /
          container.offsetWidth) *
        this.defaultStickForce
      );

      this.setState((state) => ({
        ...state,
        stick: { x: offsetX, y: offsetY },
      }));
    });
    manager.on("end", () => {
      clearInterval(interval);
      this.setState((state) => ({ ...state, stick: { x: 0, y: 0 } }));
    });
  }

  handleWheel(e) {
    e.evt.preventDefault();
    // Forward zoom to parent (index.jsx) so it zooms the whole .viewer container
    if (this.props.onZoom) {
      this.props.onZoom(e.evt.deltaY);
    }
  }

  handleStageDragMove() {
    this.setState((state) => ({ ...state }));
  }

  handleZonePointDragStart(e) {
    const id = e.target.id();
    const zonePoints = this.state.zonePoints.map((p) => ({ ...p, isDragging: p.id === id }));
    this.setState((state) => ({ ...state, zonePoints, active: id }));
  }

  handleZonePointDragMove(e) {
    const id = e.target.id();
    const idx = this.state.zonePoints.findIndex((p) => p.id === id);
    const zonePoints = [...this.state.zonePoints];
    const point = zonePoints[idx];
    e.target.x(0);
    e.target.y(0);
    const { x, y } = this._getPointerNormalized(e);
    point.x = x;
    point.y = y;
    zonePoints[idx] = point;
    this.setState((state) => ({ ...state, zonePoints: [...zonePoints] }));
  }

  handleZonePointDragEnd(e) {
    const id = e.target.id();
    const point = this.state.zonePoints.find((p) => p.id === id);
    if (point) point.isDragging = false;
  }

  handleZonePointClick(e) {
    const id = e.target.id();
    this.setState((state) => ({ ...state, active: id }));
  }

  handleResetZoom() {
    const stage = this.stage.current;
    if (!stage) return;
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    this.setState((state) => ({ ...state }));
  }

  handleSwitchMarkupX() {
    const points = this.state.points.map((point) => {
      const newHorizontalPosition = +(1 - point.x).toFixed(2);
      return { ...point, x: newHorizontalPosition };
    });

    this.setState({ ...this.state, points });
  }

  handleSwitchMarkupY() {
    const points = this.state.points.map((point) => {
      const newVerticalPosition = +(1 - point.y).toFixed(2);
      return { ...point, y: newVerticalPosition };
    });

    this.setState({ ...this.state, points });
  }

  render() {
    const container = this.container.current;
    const width = container ? container.offsetWidth : 0;
    const height = container ? container.offsetHeight : 0;
    const viewerScale = this.props.state?.viewerScale || 1;
    const actualRadius = container
      ? (width / this.defaultWidth) * this.defaultRadius / viewerScale
      : this.defaultRadius;
    const fontSize = 14 / viewerScale;
    const zoneFontSize = 10 / viewerScale;
    const lineWidth = 1 / viewerScale;

    const activePoint =
      this.state.active &&
      (this.state.points.find((point) => point.id === this.state.active) ||
       this.state.zonePoints.find((point) => point.id === this.state.active));

    const toAbsX = (x) => x * width;
    const toAbsY = (y) => y * height;

    const generateShapePoints = (points) => {
      return points.flatMap((point, i, arr) => {
        const points = [toAbsX(point.x), toAbsY(point.y)];
        if (i === arr.length - 1) {
          points.push(toAbsX(arr[0].x), toAbsY(arr[0].y));
        }
        return points;
      });
    };

    const shapePoints =
      container &&
      (this.state.points.length !== 9
        ? generateShapePoints(this.state.points)
        : generateShapePoints([
            this.state.points[7],
            this.state.points[1],
            this.state.points[0],
            this.state.points[6],
            this.state.points[7],
            this.state.points[6],
            this.state.points[5],
            this.state.points[4],
            this.state.points[7],
            this.state.points[4],
            this.state.points[3],
            this.state.points[8],
            this.state.points[7],
            this.state.points[8],
            this.state.points[2],
            this.state.points[1],
            this.state.points[7],
          ]));
    const canvasActivated = this.props.state.firstStageEditor || this.props.state.secondStageEditor;
    const isZoneMode = this.props.state.secondStageEditor;

    // Group zone points by group for drawing connecting lines
    const zoneGroups = {};
    if (isZoneMode) {
      for (const zp of this.state.zonePoints) {
        const group = this.zoneFieldPoints.find((d) => d.id === zp.id)?.group;
        if (group) {
          if (!zoneGroups[group]) zoneGroups[group] = [];
          zoneGroups[group].push(zp);
        }
      }
    }
    return (
      <>
        <div
          id="canvas-container"
          ref={this.container}
          onPointerDown={this.handleCanvasPointerDown}
          data-mode={canvasActivated ? "true" : "false"}
        >
          <div
            id="nipple-container"
            ref={this.nipple}
            style={this.nippleOptions}
          />
          <Stage
            ref={this.stage}
            width={1}
            height={1}
            opacity={canvasActivated ? 1 : 0.35}
            onWheel={this.handleWheel}
          >
            {container && (
              <Layer>
                <Rect
                  width={width}
                  height={height}
                  fill="#000000"
                  opacity={0.15}
                />
                <Line points={shapePoints} stroke="white" strokeWidth={lineWidth} tensions={1} />
                <Shape
                  sceneFunc={this.generateCustomShape}
                  fill="#00D2FF"
                  opacity={0.25}
                />
                {this.state.points.map((point) => (
                  /* Render points groups. Each group contains a circle, which represents a point,
                        and a text label with short point description. */
                  <Group
                    ref={point.ref}
                    key={`group.${point.id}`}
                    id={point.id}
                    draggable={!this.state.spaceHeld}
                    onDragStart={this.handlePointDragStart}
                    onDragMove={this.handlePointDragMove}
                    onDragEnd={this.handlePointDragEnd}
                    onClick={this.handlePointClick}
                  >
                    <Circle
                      key={`group.${point.id}.circle`}
                      id={point.id}
                      x={toAbsX(point.x)}
                      y={toAbsY(point.y)}
                      radius={actualRadius / 3}
                      hitStrokeWidth={20 / viewerScale}
                      fill={
                        this.state.active === point.id ? "white" : point.color
                      }
                      shadowColor="white"
                      shadowOffset={{ x: 1, y: 1 }}
                      shadowBlur={10}
                      shadowOpacity={this.state.active === point.id ? 1 : 0}
                    />
                    <Text
                      key={`group.${point.id}.text`}
                      id={`group.${point.id}.text`}
                      x={toAbsX(point.x) - actualRadius * 2 - 15 / viewerScale}
                      y={toAbsY(point.y) + actualRadius * 2}
                      fill="#ffffff"
                      text={`${point.desc}`}
                      fontSize={fontSize}
                      opacity={canvasActivated ? 1.0 : 0.0}
                    />
                  </Group>
                ))}
                {isZoneMode && Object.entries(zoneGroups).map(([group, pts]) => {
                  if (pts.length < 2) return null;
                  const color = pts[0].color || "#FF6B6B";
                  const linePoints = pts.flatMap((p) => [toAbsX(p.x), toAbsY(p.y)]);
                  // Close the shape for 4-point groups
                  if (pts.length >= 4) {
                    linePoints.push(toAbsX(pts[0].x), toAbsY(pts[0].y));
                  }
                  return (
                    <Line key={`zone-line-${group}`} points={linePoints} stroke={color} strokeWidth={lineWidth * 1.5} dash={[4 / viewerScale, 4 / viewerScale]} opacity={0.7} />
                  );
                })}
                {isZoneMode && this.state.zonePoints.map((point) => (
                  <Group
                    ref={point.ref}
                    key={`zgroup.${point.id}`}
                    id={point.id}
                    draggable={!this.state.spaceHeld}
                    onDragStart={this.handleZonePointDragStart}
                    onDragMove={this.handleZonePointDragMove}
                    onDragEnd={this.handleZonePointDragEnd}
                    onClick={this.handleZonePointClick}
                  >
                    <Circle
                      key={`zgroup.${point.id}.circle`}
                      id={point.id}
                      x={toAbsX(point.x)}
                      y={toAbsY(point.y)}
                      radius={actualRadius / 2.5}
                      hitStrokeWidth={20 / viewerScale}
                      fill={this.state.active === point.id ? "white" : point.color}
                      stroke="white"
                      strokeWidth={lineWidth * 0.5}
                      shadowColor="white"
                      shadowBlur={this.state.active === point.id ? 10 : 0}
                      shadowOpacity={this.state.active === point.id ? 1 : 0}
                    />
                    <Text
                      key={`zgroup.${point.id}.text`}
                      id={`zgroup.${point.id}.text`}
                      x={toAbsX(point.x) - actualRadius * 2 - 15 / viewerScale}
                      y={toAbsY(point.y) + actualRadius * 2}
                      fill="#ffffff"
                      text={point.desc}
                      fontSize={zoneFontSize}
                      opacity={canvasActivated ? 0.8 : 0.0}
                    />
                  </Group>
                ))}
                {activePoint && (
                  <Text
                    x={toAbsX(0.01)}
                    y={toAbsY(0.025)}
                    text={`Active point: ${activePoint.desc}`}
                    fill="white"
                    fontSize={fontSize}
                  />
                )}
              </Layer>
            )}
          </Stage>
        </div>
        <OverlayTrigger
          placement="top"
          delay={{ hide: 200 }}
          overlay={(props) => (
            <Tooltip id="field-markup-switch-tooltip" {...props}>
              Change orientation
            </Tooltip>
          )}
        >
          <Button
            className="field-markup-switch-y"
            variant="primary"
            size="sm"
            onClick={this.handleSwitchMarkupY}
          >
            <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          delay={{ hide: 200 }}
          overlay={(props) => (
            <Tooltip id="field-markup-switch-tooltip" {...props}>
              Change orientation
            </Tooltip>
          )}
        >
          <Button
            className="field-markup-switch-x"
            variant="success"
            size="sm"
            onClick={this.handleSwitchMarkupX}
          >
            <FontAwesomeIcon icon={["fas", "exchange-alt"]} />
          </Button>
        </OverlayTrigger>
      </>
    );
  }
}

export default memo(FieldCanvas);
